import React, { useCallback, /*useContext,*/ useEffect, useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import {
  any,
  append,
  equals,
  includes,
  last,
  mergeRight,
  propEq,
  pluck,
  split,
  toUpper,
  isEmpty,
} from "ramda";
// import { OrganizeContext } from "../../organize-context";
import {
  buildBreadcrumbs,
  isContentGroupsHome,
  OrganizeViews,
  useSearchParam,
  useView,
} from "../../organizeUtil";
import { notBlank } from "../../../../util/ext";
import { useNavigate } from "react-router-dom";
import { useDnD, useSelected } from "../../OrganizeProvider";
import clsx from "clsx";
import { useParentDrop } from "../../use-parentDrop";
import {
  useAddToModule,
  useDeleteModuleItems,
} from "../../../modules/moduleServices";
import {
  useAddToAnthology,
  useUpdateAnthologyItems,
} from "../../../anthologies/anthologyServices";
import { useAddToCollection } from "../../../collections/collectionServices";
import DragItemTypes from "../../DragItemTypes";
import { useDrag } from "react-dnd";
import { getEmptyImage } from "react-dnd-html5-backend";
import { groupChildrenByParent, setIds } from "../../organizeUtil";
import { useCreatorContext } from "../../../CreatorContext";
import { useMediaPermissions } from "../../../../util/mediaUtil";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck } from "@fortawesome/pro-regular-svg-icons";

import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import OtherActionsMenu from "./OtherActionsMenu";
import StatusBadge from "../../../common/StatusBadge";

const useStyles = makeStyles(theme => ({
  actions: {
    display: "none",
    [theme.breakpoints.up(1100)]: {
      display: "contents",
    },
  },

  card: {
    // width: 260,
    justifyContent: "space-around",
    height: 56,
    borderWidth: 1,
    borderStyle: "solid",
    borderColor: theme.palette.secondary.light,
    borderRadius: 10,
    gap: 10,
    paddingRight: 0,
    paddingLeft: 10,
    cursor: "pointer",
    "&:hover": {
      backgroundColor: theme.palette.secondary.light,
    },
  },

  disabled: {
    opacity: 0.5,
  },

  droppable: {
    backgroundColor: `${theme.palette.primary.main} !important`,
    color: "white",
  },

  icon: {
    color: "white",
  },

  mediaType: {
    fontWeight: 700,
    fontSize: "1.2rem",
    textTransform: "uppercase",
  },

  selected: {
    backgroundColor: theme.palette.primary.brandPrimary,
  },

  statusLabel: {
    fontSize: "1.2rem",
    margin: 0,
  },

  thumbnail: {
    height: 40,
    width: 40,
    borderRadius: 8,
  },

  title: {
    fontSize: "1.2rem",
    textAlign: "left",
  },

  unselected: {
    backgroundColor: theme.palette.secondary.light,
  },
}));

const statusBadgeLabel = {
  ACTIVE: "Live",
  INACTIVE: "Draft",
};

const getViewPath = ({ searchParam, group }) => {
  const viewParam = searchParam.get("view");
  const idParam = searchParam.get("id");
  const currentView = last(split(",", viewParam));

  // Catch ungrouped anthologies and collections from organize home screen
  const previousView =
    isContentGroupsHome(currentView) && isEmpty(group.parentModules)
      ? [OrganizeViews.ORGANIZE, OrganizeViews.UNGROUPED_ITEMS]
      : split(",", viewParam);

  const views = append(group.mediaType, previousView);
  const ids = notBlank(idParam)
    ? append(group.id, split(",", idParam))
    : [group.id];

  const breadcrumb = last(buildBreadcrumbs({ views, ids }));

  return breadcrumb.viewPath;
};

const dragTypes = {
  module: DragItemTypes.MODULE,
  anthology: DragItemTypes.ANTHOLOGY,
  collection: DragItemTypes.COLLECTION,
};

const acceptableDrops = {
  module: [
    DragItemTypes.ANTHOLOGY,
    DragItemTypes.COLLECTION,
    DragItemTypes.CONTENT,
  ],
  anthology: [DragItemTypes.COLLECTION],
  collection: [DragItemTypes.CONTENT],
};

const DraggableContentGroupCard = props => {
  const {
    group,
    parent,
    parentPrevMediaList,
    removeFromParent,
    showAlert,
  } = props;

  const classes = useStyles();
  const searchParam = useSearchParam();
  const navigate = useNavigate();
  const view = useView();

  const [accept, setAccept] = useState([]);
  const [type, setType] = useState("");
  useEffect(() => {
    setAccept(acceptableDrops[group.mediaType]);
    setType(dragTypes[group.mediaType]);
  }, [group]);

  const {
    removeAllSelected,
    selectedContentGroups,
    updateSelectedContentGroups,
  } = useSelected();
  const dnd = useDnD();

  const creatorContext = useCreatorContext();
  const { readOnly } = useMediaPermissions(group);

  const addToModule = useAddToModule();
  const addToAnthology = useAddToAnthology();
  const updateAnthologyItems = useUpdateAnthologyItems();
  const addToCollection = useAddToCollection();
  const deleteModuleItems = useDeleteModuleItems();
  const onDrop = useCallback(
    async ({ items, dropAction }) => {
      try {
        const contentMediaIds = pluck("id", items);

        const addToGroup = {
          module: addToModule,
          anthology: addToAnthology,
          collection: addToCollection,
        };

        if (equals(dropAction, "move")) {
          const prevParents = groupChildrenByParent(items);

          // Remove children from previous parents
          for (const { prevParent, newChildren } of prevParents) {
            if (notBlank(prevParent.id)) {
              const ids = setIds({
                mediaType: prevParent.mediaType,
                prevMediaList: prevParent.prevMediaList,
                itemsToRemove: newChildren.ids,
              });

              const updateBody = { id: prevParent.id, ids };

              if (equals(prevParent.mediaType, "anthology")) {
                await updateAnthologyItems.mutateAsync({
                  resourceIds: { id: prevParent.id },
                  data: { ids: ids },
                });
              }

              if (equals(prevParent.mediaType, "module")) {
                await deleteModuleItems.mutateAsync(updateBody);
              }
            }
          }
        }

        await addToGroup[group.mediaType].mutateAsync({
          resourceIds: {
            id: group.id,
          },
          data: {
            ids: contentMediaIds,
          },
        });

        const alert = equals(dropAction, "move")
          ? "Item(s) Successfully Moved"
          : "Item(s) Successfully Added";

        showAlert("success", [alert]);
      } catch (err) {
        showAlert("error", [err.message]);
      }
    },
    [
      addToAnthology,
      addToCollection,
      addToModule,
      deleteModuleItems,
      group,
      showAlert,
      updateAnthologyItems,
    ],
  );

  const [{ isOver, canDrop, contentIsDragging }, drop] = useParentDrop({
    parent: group,
    onDrop,
    accept,
  });

  const handleClick = useCallback(
    event => {
      if (!equals(group.mediaType, "module")) {
        if (event.detail === 1 && !readOnly) {
          updateSelectedContentGroups(
            mergeRight(group, { parent, parentPrevMediaList }),
          );
        }
      }

      if (event.detail === 2) {
        removeAllSelected();
        const viewPath = getViewPath({ searchParam, group });
        navigate(viewPath);
      }
    },
    [
      parent,
      parentPrevMediaList,
      group,
      navigate,
      readOnly,
      removeAllSelected,
      searchParam,
      updateSelectedContentGroups,
    ],
  );

  const [, drag, preview] = useDrag(
    () => ({
      type,
      item: {
        id: group.id,
        dragAction: isContentGroupsHome(view) ? "copy" : "move",
        mediaType: group.mediaType,
        parent,
        prevMediaList: parentPrevMediaList,
      },
      end: () => dnd.updateDragAction(""),
      canDrag: monitor => !equals(monitor.getItemType(), DragItemTypes.MODULE),
      collect: monitor => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [type],
  );

  // Create Draggable & Droppable Element
  // https://github.com/react-dnd/react-dnd/issues/1451
  const dragDropRef = useCallback(
    node => {
      drag(node);
      drop(node);
    },
    [drag, drop],
  );

  // This is required for creating a custom drag overlay
  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  return (
    <Grid
      ref={dragDropRef}
      onClick={handleClick}
      component={Button}
      className={clsx(classes.card, {
        [classes.droppable]: isOver && canDrop,
        [classes.disabled]:
          contentIsDragging && equals(group.mediaType, "anthology"),
      })}
      sx={{ color: "black" }}
      container
      alignItems="center"
      justifyContent="space-around"
    >
      <Grid item>
        <Box
          className={clsx({
            [classes.selected]: any(
              propEq("id", group.id),
              selectedContentGroups,
            ),
            [classes.unselected]: !any(
              propEq("id", group.id),
              selectedContentGroups,
            ),
          })}
          height={40}
          width={40}
          borderRadius="8px"
          display="flex"
          justifyContent="center"
          alignItems="center"
        >
          {any(propEq("id", group.id), selectedContentGroups) ? (
            <FontAwesomeIcon icon={faCheck} className={classes.icon} />
          ) : (
            <Avatar
              className={classes.thumbnail}
              src={
                includes(group.id, creatorContext.updatedThumbnails)
                  ? `${group.thumbnailUrl}?&tc=${Date.now()}`
                  : group.thumbnailUrl
              }
              alt={`${group.name} thumbnail`}
            />
          )}
        </Box>
      </Grid>
      <Grid item xs={6} md={7}>
        {group && (
          <Grid container justifyContent="space-between" alignItems="center">
            <Typography className={classes.mediaType} noWrap>
              {group.mediaType}
            </Typography>

            {equals(toUpper(group.mediaType), "MODULE") && (
              <Grid item>
                <StatusBadge
                  size="xs"
                  status={group.status}
                  label={
                    <p className={classes.statusLabel}>
                      {statusBadgeLabel[group.status]}
                    </p>
                  }
                />
              </Grid>
            )}
          </Grid>
        )}
        <Typography className={classes.title} noWrap>
          {group.name}
        </Typography>
      </Grid>
      <Grid item xs="auto" className={classes.actions}>
        <OtherActionsMenu
          group={mergeRight(group, {
            parent,
            prevMediaList: parentPrevMediaList,
          })}
          removeFromParent={removeFromParent}
        />
      </Grid>
    </Grid>
  );
};

export default React.memo(DraggableContentGroupCard);
